import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import Integration from "@/model/interface/Integration";
import IntegrationSyncToggle from "@/components/pages/integrations/IntegrationSyncToggle";
import {
  useUpdateIntegrationSettingsMutation,
  useUpdateIntegrationSyncSettingsMutation,
  useSyncIntegrationMutation,
} from "@/store/rest_api";
import toast from "react-hot-toast";

interface Props {
  integration: Integration;
}

export default function GhlSettings({ integration }: Props) {
  const [updateSettings] = useUpdateIntegrationSettingsMutation();
  const [updateSyncSettings] = useUpdateIntegrationSyncSettingsMutation();
  const [syncIntegration, { isLoading: isSyncing }] = useSyncIntegrationMutation();
  const settings = integration.settings || {};
  const [leadTag, setLeadTag] = useState<string>((settings.leadTag as string) ?? "lead");

  const updateSetting = async (key: string, value: unknown) => {
    try {
      await updateSettings({ id: integration.id, settings: { [key]: value } }).unwrap();
    } catch {
      toast.error("Failed to update setting");
    }
  };

  const updateSyncDirection = async (value: string) => {
    try {
      await updateSyncSettings({
        id: integration.id,
        syncEnabled: integration.syncEnabled !== false,
        syncDirection: value,
      }).unwrap();
    } catch {
      toast.error("Failed to update sync direction");
    }
  };

  const handleSync = async () => {
    try {
      await syncIntegration(integration).unwrap();
      toast.success("Contact sync completed");
    } catch {
      toast.error("Sync failed");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Connection</CardTitle>
          <CardDescription>GoHighLevel connection status and sync</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <IntegrationSyncToggle integration={integration} />

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Status</p>
              <p className="text-sm text-muted-foreground">
                {integration.lastSyncAt
                  ? `Last synced ${new Date(integration.lastSyncAt).toLocaleString()}`
                  : "Never synced"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={integration.status === "active" ? "default" : "secondary"}>{integration.status}</Badge>
              <Button
                onClick={handleSync}
                disabled={isSyncing || integration.syncEnabled === false || integration.status !== "active"}
                size="sm"
              >
                {isSyncing ? "Syncing..." : "Sync Now"}
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Sync Direction</p>
              <p className="text-sm text-muted-foreground">Controls whether contacts are pulled, pushed, or both</p>
            </div>
            <Select
              value={integration.syncDirection || "BIDIRECTIONAL"}
              onValueChange={(value) => updateSyncDirection(value)}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BIDIRECTIONAL">Bidirectional</SelectItem>
                <SelectItem value="PULL_ONLY">Pull Only</SelectItem>
                <SelectItem value="PUSH_ONLY">Push Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Lead Marking</CardTitle>
          <CardDescription>Tag applied to new lead contacts when they are created in GoHighLevel</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-w-sm">
            <Label htmlFor="leadTag">Lead tag</Label>
            <Input
              id="leadTag"
              placeholder="lead"
              value={leadTag}
              onChange={(e) => setLeadTag(e.target.value)}
              onBlur={() => updateSetting("leadTag", leadTag.trim())}
            />
            <p className="text-xs text-muted-foreground">
              Added to contacts of type Lead on creation. Leave empty to disable.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
